import { ImagePlus } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { useUploadHomepageImage } from '../../api'

/** A button that uploads a homepage picture and reports its stored-file id and public address. */
export function PictureUpload({
  label,
  onUploaded,
  disabled,
}: {
  label: string
  onUploaded: (file: { fileId: number; url: string | null }) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('homepage')
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadHomepageImage()
  const [error, setError] = useState<string | null>(null)

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    try {
      const stored = await upload.mutateAsync(file)
      onUploaded({ fileId: stored.id, url: stored.url })
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-label={label}
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<ImagePlus />}
        loading={upload.isPending}
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        {label}
      </Button>
      <p className="mt-1.5 text-xs text-muted">{t('JPG, PNG or WebP · max 5 MB')}</p>
      {error ? (
        <p className="mt-1 text-xs font-medium text-danger-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
